export const CANONICAL_GAMES = Object.freeze({
  PUBG_MOBILE: 'pubg_mobile',
  COD_MOBILE: 'cod_mobile',
  FREE_FIRE: 'free_fire',
});

export const DEFAULT_CANONICAL_GAME = CANONICAL_GAMES.PUBG_MOBILE;

const PROFILE_VERSION = 'mg4a_final_profiles_v1';

const OFFICIAL_GAME_PROFILES = Object.freeze({
  [CANONICAL_GAMES.PUBG_MOBILE]: Object.freeze({
    id: CANONICAL_GAMES.PUBG_MOBILE,
    version: PROFILE_VERSION,
    legacyContextKey: 'pubg',
    aliases: ['pubg_mobile', 'pubg', 'pubgm', 'pubg-mobile'],
    coachProfile: Object.freeze({
      routingKey: 'coach_pubg_mobile',
      engineProfile: CANONICAL_GAMES.PUBG_MOBILE,
      primaryFocus: 'recoil_control',
      gameplayCharacteristics: Object.freeze([
        'tactical_positioning',
        'peek_discipline',
        'spray_stability_under_scope',
        'mid_long_range_control',
      ]),
      mechanicalPriorities: Object.freeze({
        recoil_stability: 0.34,
        crosshair_height_consistency: 0.2,
        tracking_under_recoil: 0.19,
        first_shot_alignment: 0.15,
        transition_control: 0.12,
      }),
      tacticalPriorities: Object.freeze({
        angle_isolation: 0.3,
        cover_usage: 0.26,
        rotation_timing: 0.24,
        engagement_selection: 0.2,
      }),
      keySkillWeights: Object.freeze({
        aim: 0.24,
        recoil: 0.34,
        movement: 0.12,
        peek: 0.14,
        positioning: 0.16,
      }),
      weightingModel: Object.freeze({
        mechanical_execution: 0.52,
        tactical_discipline: 0.30,
        engagement_selection: 0.10,
        consistency_pressure: 0.08,
      }),
      errorSeverityModel: Object.freeze({
        critical: Object.freeze({
          patterns: Object.freeze([
            'full_spray_loss_after_initial_bullets',
            'repeated_open_peek_without_cover',
            'crosshair_offline_before_contact',
          ]),
          impact: 'fight_losing',
        }),
        major: Object.freeze({
          patterns: Object.freeze([
            'unstable_scope_transition_3x_4x',
            'late_recoil_compensation_in_mid_range',
            'overwide_peek_timing',
          ]),
          impact: 'high_duel_risk',
        }),
        moderate: Object.freeze({
          patterns: Object.freeze([
            'vertical_drift_after_commit',
            'inconsistent_recenter_between_targets',
            'entry_angle_without_isolation',
          ]),
          impact: 'performance_drop',
        }),
        minor: Object.freeze({
          patterns: Object.freeze([
            'slow_target_swap_after_knock',
            'micro_misalign_on_first_bullet',
          ]),
          impact: 'efficiency_loss',
        }),
      }),
      recommendationStyle: Object.freeze({
        format: 'precision_corrections',
        sentenceLength: 'short',
        intensity: 'technical',
        includeScopeSpecificFixes: true,
        voice: 'disciplined_and_precise',
        maxCorrectionsPerBlock: 3,
      }),
      videoAnalysisEmphasis: Object.freeze({
        recoil_windows: 'high',
        scope_transitions: 'high',
        movement_to_shot_sync: 'medium',
        utility_or_macro_decisions: 'medium',
        crosshair_height_before_contact: 'high',
        peek_exit_control: 'medium',
      }),
    }),
    sensitivityProfile: Object.freeze({
      routingKey: 'sensi_pubg_mobile',
      adapterKey: CANONICAL_GAMES.PUBG_MOBILE,
      defaultWeapon: 'M416',
      fieldSet: 'pubg_scopes',
      fieldGroups: Object.freeze({
        camera: Object.freeze(['no_scope', 'red_dot', 'scope2x', 'scope3x', 'scope4x', 'scope6x', 'scope8x']),
        ads: Object.freeze(['no_scope', 'red_dot', 'scope2x', 'scope3x', 'scope4x', 'scope6x']),
        gyro: Object.freeze(['no_scope', 'red_dot', 'scope2x', 'scope3x', 'scope4x', 'scope6x']),
        scopePriority: Object.freeze(['scope3x', 'scope4x', 'scope6x']),
      }),
      supportedCategories: Object.freeze(['camera', 'ads', 'scopes', 'gyro', 'scope_priority']),
      adjustmentStrategy: Object.freeze({
        primaryFields: Object.freeze(['camera.no_scope', 'ads.no_scope', 'scope3x', 'scope4x']),
        secondaryFields: Object.freeze(['scope2x', 'scope6x', 'gyro.no_scope']),
        coupledAdjustments: Object.freeze([
          Object.freeze({ leader: 'camera.no_scope', follower: 'ads.no_scope', ratio: 0.7 }),
          Object.freeze({ leader: 'scope3x', follower: 'scope4x', ratio: 0.85 }),
        ]),
        protectedFields: Object.freeze(['scope6x', 'scope8x']),
        aggressiveChangeForbidden: Object.freeze(['scope4x', 'scope6x', 'scope8x']),
      }),
      safeAdjustmentRanges: Object.freeze({
        camera_step: Object.freeze({ min: -10, max: 10, step: 2 }),
        ads_step: Object.freeze({ min: -8, max: 8, step: 2 }),
        gyro_step: Object.freeze({ min: -10, max: 10, step: 2 }),
        scoped_fine_tune: Object.freeze({ min: -6, max: 6, step: 1 }),
      }),
      fieldCalibrationBands: Object.freeze({
        no_scope: Object.freeze({ safe: Object.freeze([-10, 10]), conservative: Object.freeze([-6, 6]), highRiskWarning: Object.freeze([-14, 14]) }),
        red_dot: Object.freeze({ safe: Object.freeze([-10, 10]), conservative: Object.freeze([-6, 6]), highRiskWarning: Object.freeze([-14, 14]) }),
        scope2x: Object.freeze({ safe: Object.freeze([-8, 8]), conservative: Object.freeze([-5, 5]), highRiskWarning: Object.freeze([-12, 12]) }),
        scope3x: Object.freeze({ safe: Object.freeze([-8, 8]), conservative: Object.freeze([-4, 4]), highRiskWarning: Object.freeze([-10, 10]) }),
        scope4x: Object.freeze({ safe: Object.freeze([-6, 6]), conservative: Object.freeze([-3, 3]), highRiskWarning: Object.freeze([-8, 8]) }),
        scope6x: Object.freeze({ safe: Object.freeze([-4, 4]), conservative: Object.freeze([-2, 2]), highRiskWarning: Object.freeze([-6, 6]) }),
        scope8x: Object.freeze({ safe: Object.freeze([-4, 4]), conservative: Object.freeze([-2, 2]), highRiskWarning: Object.freeze([-5, 5]) }),
      }),
      defaultOptimizationStrategy: Object.freeze({
        anchor: 'recoil_pattern_stability',
        firstPass: 'camera_and_ads_balance',
        secondPass: 'scope_specific_refinement',
        finalPass: 'gyro_micro_tuning',
      }),
      optimizationLogic: Object.freeze({
        primaryObjective: 'stability_first',
        secondaryObjective: 'recoil_control_under_scope',
        tertiaryObjective: 'micro_speed_without_horizontal_loss',
        gyroBias: 'moderate_if_player_uses_gyro',
      }),
      calibrationNotes: Object.freeze([
        'Prioritize spray stability over raw speed.',
        'Apply small scope-specific corrections after base camera/ads convergence.',
        'Avoid aggressive jumps on 3x/4x/6x in early iterations.',
      ]),
      gameSpecificNotes: Object.freeze([
        'PUBG punishes unstable 3x/4x spray more than slightly slower camera speed.',
        'Most players improve faster by stabilizing no-scope and 3x before touching high zoom.',
        'Avoid symmetric high jumps across all scopes; refine by engagement distance.',
      ]),
    }),
    trainingProfile: Object.freeze({
      routingKey: 'training_pubg_mobile',
      datasetKey: CANONICAL_GAMES.PUBG_MOBILE,
      priorityTrack: Object.freeze(['recoil', 'positioning', 'peek', 'crosshair']),
      skillPriorities: Object.freeze({
        recoil: 0.34,
        positioning: 0.2,
        peek: 0.18,
        crosshair: 0.16,
        movement: 0.12,
      }),
      recommendedDrillCategories: Object.freeze([
        'recoil',
        'peek',
        'positioning',
        'crosshair',
        'rotation',
      ]),
      correctionPriorities: Object.freeze([
        'stabilize_spray_before_speed',
        'preserve_cover_on_peek',
        'reduce_vertical_drift_in_scope',
      ]),
      postAnalysisMapping: Object.freeze({
        recoil_low: Object.freeze(['recoil', 'crosshair']),
        positioning_low: Object.freeze(['positioning', 'rotation']),
        timing_low: Object.freeze(['peek', 'entry']),
      }),
    }),
    miTone: Object.freeze({
      style: 'precision',
      tempo: 'tactical',
      personaHint: 'stable_control',
      communicationStyle: 'technical_and_structured',
      directiveStyle: 'surgical_next_step',
      vocabularyTone: 'tactical_precision',
      coachingAttitude: 'calm_command',
    }),
    uiTerminology: Object.freeze({
      gameName: 'PUBG Mobile',
      systemLabels: Object.freeze({
        coach: 'Análise Tatica',
        sensi: 'Estabilizador',
        training: 'Protocolo de Campo',
      }),
      directives: Object.freeze({
        recharge: 'Core descarregado. Recharger mandatorio para calibracao.',
        analysis: 'Dados insuficientes. Sincronize replay tatico.',
        training: 'Oscilacao detectada. Execute protocolo de recoil.',
        sensi: 'Instabilidade de hardware. Inicie calibracao.',
      }),
      terms: Object.freeze({
        headshot: 'Headshot',
        recoil: 'Recoil',
        aim: 'Precisao',
        movement: 'Rotacao',
      }),
    }),
  }),
  [CANONICAL_GAMES.COD_MOBILE]: Object.freeze({
    id: CANONICAL_GAMES.COD_MOBILE,
    version: PROFILE_VERSION,
    legacyContextKey: 'cod',
    aliases: ['cod_mobile', 'codm', 'cod', 'call_of_duty_mobile', 'cod-mobile'],
    coachProfile: Object.freeze({
      routingKey: 'coach_cod_mobile',
      engineProfile: CANONICAL_GAMES.COD_MOBILE,
      primaryFocus: 'ads_speed_and_hybrid_tracking',
      gameplayCharacteristics: Object.freeze([
        'high_tempo_duels',
        'ads_snap_control',
        'hybrid_tracking_with_micro_flick',
        'close_mid_range_pressure',
      ]),
      mechanicalPriorities: Object.freeze({
        ads_entry_speed: 0.3,
        snap_accuracy: 0.24,
        tracking_consistency: 0.22,
        strafe_shot_sync: 0.14,
        recoil_recovery: 0.1,
      }),
      tacticalPriorities: Object.freeze({
        duel_tempo_control: 0.34,
        lane_isolation: 0.24,
        timing_on_rechallenge: 0.22,
        pressure_windows: 0.2,
      }),
      keySkillWeights: Object.freeze({
        aim: 0.3,
        recoil: 0.14,
        movement: 0.24,
        peek: 0.18,
        positioning: 0.14,
      }),
      weightingModel: Object.freeze({
        mechanical_execution: 0.56,
        tactical_discipline: 0.22,
        engagement_tempo: 0.14,
        consistency_pressure: 0.08,
      }),
      errorSeverityModel: Object.freeze({
        critical: Object.freeze({
          patterns: Object.freeze([
            'ads_late_entry_in_first_contact',
            'snap_overshoot_without_recovery',
            'full_commit_without_cover_trade_path',
          ]),
          impact: 'immediate_duel_loss',
        }),
        major: Object.freeze({
          patterns: Object.freeze([
            'unstable_snap_to_track_transition',
            'rechallenge_timing_without_reset',
            'lane_entry_with_exposed_shoulder',
          ]),
          impact: 'high_death_risk',
        }),
        moderate: Object.freeze({
          patterns: Object.freeze([
            'micro_strafe_desync',
            'late_recenter_after_kill',
            'inconsistent_prefire_alignment',
          ]),
          impact: 'tempo_drop',
        }),
        minor: Object.freeze({
          patterns: Object.freeze([
            'unnecessary_scope_hold_time',
            'delayed_target_swap',
          ]),
          impact: 'pressure_loss',
        }),
      }),
      recommendationStyle: Object.freeze({
        format: 'aggressive_duel_microfixes',
        sentenceLength: 'short',
        intensity: 'high_tempo',
        includeTempoCalls: true,
        voice: 'direct_and_competitive',
        maxCorrectionsPerBlock: 3,
      }),
      videoAnalysisEmphasis: Object.freeze({
        ads_activation_timing: 'high',
        snap_to_tracking_transition: 'high',
        close_duel_pathing: 'medium',
        macro_rotation: 'low',
        strafe_to_shot_sync: 'high',
        rechallenge_window_control: 'medium',
      }),
    }),
    sensitivityProfile: Object.freeze({
      routingKey: 'sensi_cod_mobile',
      adapterKey: CANONICAL_GAMES.COD_MOBILE,
      defaultWeapon: 'M4',
      fieldSet: 'cod_ads',
      fieldGroups: Object.freeze({
        camera: Object.freeze(['standard', 'tactical', 'third_person']),
        ads: Object.freeze(['ads_standard', 'ads_tactical', 'ads_sniper']),
        sniper: Object.freeze(['sniper_low_zoom', 'sniper_high_zoom']),
        gyro: Object.freeze(['gyro_standard', 'gyro_tactical', 'gyro_sniper']),
        fov: Object.freeze(['fov']),
      }),
      supportedCategories: Object.freeze(['camera', 'ads', 'sniper', 'gyro', 'fov']),
      adjustmentStrategy: Object.freeze({
        primaryFields: Object.freeze(['ads_standard', 'ads_tactical', 'camera.standard']),
        secondaryFields: Object.freeze(['gyro_standard', 'sniper_low_zoom', 'fov']),
        coupledAdjustments: Object.freeze([
          Object.freeze({ leader: 'ads_standard', follower: 'ads_tactical', ratio: 0.8 }),
          Object.freeze({ leader: 'camera.standard', follower: 'ads_standard', ratio: 0.6 }),
        ]),
        protectedFields: Object.freeze(['sniper_high_zoom', 'fov']),
        aggressiveChangeForbidden: Object.freeze(['fov', 'sniper_high_zoom']),
      }),
      safeAdjustmentRanges: Object.freeze({
        camera_step: Object.freeze({ min: -8, max: 8, step: 2 }),
        ads_step: Object.freeze({ min: -10, max: 10, step: 2 }),
        sniper_step: Object.freeze({ min: -6, max: 6, step: 1 }),
        gyro_step: Object.freeze({ min: -10, max: 10, step: 2 }),
        fov_step: Object.freeze({ min: -4, max: 4, step: 1 }),
      }),
      fieldCalibrationBands: Object.freeze({
        'camera.standard': Object.freeze({ safe: Object.freeze([-8, 8]), conservative: Object.freeze([-5, 5]), highRiskWarning: Object.freeze([-12, 12]) }),
        ads_standard: Object.freeze({ safe: Object.freeze([-10, 10]), conservative: Object.freeze([-6, 6]), highRiskWarning: Object.freeze([-14, 14]) }),
        ads_tactical: Object.freeze({ safe: Object.freeze([-10, 10]), conservative: Object.freeze([-6, 6]), highRiskWarning: Object.freeze([-14, 14]) }),
        sniper_low_zoom: Object.freeze({ safe: Object.freeze([-6, 6]), conservative: Object.freeze([-3, 3]), highRiskWarning: Object.freeze([-9, 9]) }),
        sniper_high_zoom: Object.freeze({ safe: Object.freeze([-4, 4]), conservative: Object.freeze([-2, 2]), highRiskWarning: Object.freeze([-6, 6]) }),
        gyro_standard: Object.freeze({ safe: Object.freeze([-8, 8]), conservative: Object.freeze([-4, 4]), highRiskWarning: Object.freeze([-12, 12]) }),
        fov: Object.freeze({ safe: Object.freeze([-4, 4]), conservative: Object.freeze([-2, 2]), highRiskWarning: Object.freeze([-5, 5]) }),
      }),
      defaultOptimizationStrategy: Object.freeze({
        anchor: 'ads_entry_and_lock',
        firstPass: 'ads_and_tactical_alignment',
        secondPass: 'sniper_and_gyro_refinement',
        finalPass: 'fov_tempo_balance',
      }),
      optimizationLogic: Object.freeze({
        primaryObjective: 'ads_speed_first',
        secondaryObjective: 'snap_control_without_overshoot',
        tertiaryObjective: 'tracking_stability_during_strafe',
        gyroBias: 'adaptive_to_player_gyro_confidence',
      }),
      calibrationNotes: Object.freeze([
        'Protect ADS speed while controlling overshoot.',
        'Treat sniper sensitivity independently from AR/SMG ADS.',
        'Keep FOV changes conservative until tracking is stable.',
      ]),
      gameSpecificNotes: Object.freeze([
        'CODM duels are tempo-driven, so over-stabilizing can hurt entry speed.',
        'Sniper tuning must stay isolated from AR/SMG profiles to avoid cross-impact.',
        'FOV changes should be the last pass after ADS and tracking are stable.',
      ]),
    }),
    trainingProfile: Object.freeze({
      routingKey: 'training_cod_mobile',
      datasetKey: CANONICAL_GAMES.COD_MOBILE,
      priorityTrack: Object.freeze(['tracking', 'movement', 'reaction', 'entry']),
      skillPriorities: Object.freeze({
        tracking: 0.28,
        reaction: 0.24,
        movement: 0.22,
        aim: 0.16,
        positioning: 0.1,
      }),
      recommendedDrillCategories: Object.freeze([
        'tracking',
        'prefire',
        'entry',
        'pressure',
        'movement',
      ]),
      correctionPriorities: Object.freeze([
        'accelerate_ads_opening_without_overshoot',
        'stabilize_snap_to_track_transition',
        'improve_duel_reengage_timing',
      ]),
      postAnalysisMapping: Object.freeze({
        tracking_low: Object.freeze(['tracking', 'prefire']),
        reaction_low: Object.freeze(['pressure', 'entry']),
        movement_low: Object.freeze(['movement', 'cqb']),
      }),
    }),
    miTone: Object.freeze({
      style: 'aggressive',
      tempo: 'fast',
      personaHint: 'snap_and_tracking',
      communicationStyle: 'direct_and_urgent',
      directiveStyle: 'immediate_duel_action',
      vocabularyTone: 'competitive_duel',
      coachingAttitude: 'high_energy_command',
    }),
    uiTerminology: Object.freeze({
      gameName: 'Call of Duty: Mobile',
      systemLabels: Object.freeze({
        coach: 'Análise Pro',
        sensi: 'Otimizador ADS',
        training: 'Drills de Reacao',
      }),
      directives: Object.freeze({
        recharge: 'Sistema offline. Recharger necessario para scan.',
        analysis: 'Sem telemetria. Sincronize gameplay ranked.',
        training: 'Snap lento detectado. Inicie drills de reacao.',
        sensi: 'ADS instavel. Calibre seu tempo de resposta.',
      }),
      terms: Object.freeze({
        headshot: 'Headshot',
        recoil: 'Recoil',
        aim: 'ADS',
        movement: 'Strafe',
      }),
    }),
  }),
  [CANONICAL_GAMES.FREE_FIRE]: Object.freeze({
    id: CANONICAL_GAMES.FREE_FIRE,
    version: PROFILE_VERSION,
    legacyContextKey: 'freefire',
    aliases: ['free_fire', 'freefire', 'ff', 'free-fire'],
    coachProfile: Object.freeze({
      routingKey: 'coach_free_fire',
      engineProfile: CANONICAL_GAMES.FREE_FIRE,
      primaryFocus: 'rush_capa_precision',
      gameplayCharacteristics: Object.freeze([
        'rush_pressure',
        'fast_target_acquisition',
        'headline_snap',
        'close_range_duel_commit',
      ]),
      mechanicalPriorities: Object.freeze({
        first_pixel_acquisition: 0.28,
        vertical_snap_to_headline: 0.3,
        close_range_control: 0.22,
        movement_shot_timing: 0.12,
        recoil_recovery: 0.08,
      }),
      tacticalPriorities: Object.freeze({
        entry_timing: 0.32,
        rush_path_selection: 0.28,
        pressure_commitment: 0.24,
        fallback_positioning: 0.16,
      }),
      keySkillWeights: Object.freeze({
        aim: 0.32,
        recoil: 0.12,
        movement: 0.26,
        peek: 0.14,
        positioning: 0.16,
      }),
      weightingModel: Object.freeze({
        mechanical_execution: 0.58,
        tactical_discipline: 0.18,
        engagement_tempo: 0.16,
        consistency_pressure: 0.08,
      }),
      errorSeverityModel: Object.freeze({
        critical: Object.freeze({
          patterns: Object.freeze([
            'first_contact_miss_in_close_range',
            'headline_snap_break_with_full_exposure',
            'rush_commit_without_escape_line',
          ]),
          impact: 'instant_knock_risk',
        }),
        major: Object.freeze({
          patterns: Object.freeze([
            'overflick_after_initial_contact',
            'cqb_tracking_break_after_jump_or_slide',
            'late_crosshair_raise_to_headline',
          ]),
          impact: 'fight_flip_risk',
        }),
        moderate: Object.freeze({
          patterns: Object.freeze([
            'rush_path_without_cover_segment',
            'inconsistent_second_target_transfer',
            'entry_stop_before_commit_window',
          ]),
          impact: 'momentum_loss',
        }),
        minor: Object.freeze({
          patterns: Object.freeze([
            'slow_micro_correction_after_hit_marker',
            'extra_exposure_after_win',
          ]),
          impact: 'efficiency_drop',
        }),
      }),
      recommendationStyle: Object.freeze({
        format: 'high_impact_microcalls',
        sentenceLength: 'short',
        intensity: 'assertive',
        includeRushExecutionCalls: true,
        voice: 'practical_and_explosive',
        maxCorrectionsPerBlock: 3,
      }),
      videoAnalysisEmphasis: Object.freeze({
        first_contact_response: 'high',
        close_range_crosshair_adjustment: 'high',
        recoil_window: 'medium',
        long_range_macro: 'low',
        headline_conversion_window: 'high',
        commit_or_cancel_rush_timing: 'medium',
      }),
    }),
    sensitivityProfile: Object.freeze({
      routingKey: 'sensi_free_fire',
      adapterKey: CANONICAL_GAMES.FREE_FIRE,
      defaultWeapon: 'SCAR-L',
      fieldSet: 'free_fire_scopes',
      fieldGroups: Object.freeze({
        camera: Object.freeze(['general', 'red_dot', 'scope2x', 'scope4x', 'free_look']),
        ads: Object.freeze(['general', 'red_dot', 'scope2x', 'scope4x', 'sniper']),
        scope: Object.freeze(['scope2x', 'scope4x', 'sniper']),
        closeRangeBias: Object.freeze(['general', 'red_dot']),
      }),
      supportedCategories: Object.freeze(['general', 'scopes', 'sniper', 'close_range_control', 'free_look']),
      adjustmentStrategy: Object.freeze({
        primaryFields: Object.freeze(['general', 'red_dot', 'scope2x']),
        secondaryFields: Object.freeze(['scope4x', 'free_look']),
        coupledAdjustments: Object.freeze([
          Object.freeze({ leader: 'general', follower: 'red_dot', ratio: 0.9 }),
          Object.freeze({ leader: 'scope2x', follower: 'scope4x', ratio: 0.75 }),
        ]),
        protectedFields: Object.freeze(['sniper', 'free_look']),
        aggressiveChangeForbidden: Object.freeze(['sniper']),
      }),
      safeAdjustmentRanges: Object.freeze({
        general_step: Object.freeze({ min: -12, max: 12, step: 2 }),
        red_dot_step: Object.freeze({ min: -12, max: 12, step: 2 }),
        scope_step: Object.freeze({ min: -8, max: 8, step: 2 }),
        sniper_step: Object.freeze({ min: -6, max: 6, step: 1 }),
      }),
      fieldCalibrationBands: Object.freeze({
        general: Object.freeze({ safe: Object.freeze([-12, 12]), conservative: Object.freeze([-8, 8]), highRiskWarning: Object.freeze([-16, 16]) }),
        red_dot: Object.freeze({ safe: Object.freeze([-12, 12]), conservative: Object.freeze([-8, 8]), highRiskWarning: Object.freeze([-16, 16]) }),
        scope2x: Object.freeze({ safe: Object.freeze([-8, 8]), conservative: Object.freeze([-5, 5]), highRiskWarning: Object.freeze([-12, 12]) }),
        scope4x: Object.freeze({ safe: Object.freeze([-6, 6]), conservative: Object.freeze([-3, 3]), highRiskWarning: Object.freeze([-9, 9]) }),
        sniper: Object.freeze({ safe: Object.freeze([-6, 6]), conservative: Object.freeze([-3, 3]), highRiskWarning: Object.freeze([-8, 8]) }),
        free_look: Object.freeze({ safe: Object.freeze([-8, 8]), conservative: Object.freeze([-4, 4]), highRiskWarning: Object.freeze([-12, 12]) }),
      }),
      defaultOptimizationStrategy: Object.freeze({
        anchor: 'fast_target_pickup_with_controlled_snap',
        firstPass: 'general_and_red_dot_stability',
        secondPass: 'scope_2x_4x_balance',
        finalPass: 'sniper_and_fine_adjust',
      }),
      optimizationLogic: Object.freeze({
        primaryObjective: 'close_range_acquisition_first',
        secondaryObjective: 'headline_snap_control',
        tertiaryObjective: 'stability_after_first_bullet',
        gyroBias: 'low_or_optional',
      }),
      calibrationNotes: Object.freeze([
        'Bias toward fast acquisition but cap overflick risk.',
        'Validate capa consistency before increasing speed.',
        'Keep sniper tuning independent from rush profile.',
      ]),
      gameSpecificNotes: Object.freeze([
        'Free Fire rewards fast first-contact control, especially in close-range rush exchanges.',
        'Excessively high general speed can inflate capa inconsistency under pressure.',
        'Sniper and free-look should move conservatively to avoid destabilizing rush tuning.',
      ]),
    }),
    trainingProfile: Object.freeze({
      routingKey: 'training_free_fire',
      datasetKey: CANONICAL_GAMES.FREE_FIRE,
      priorityTrack: Object.freeze(['snap', 'rush', 'movement', 'cqb']),
      skillPriorities: Object.freeze({
        snap: 0.3,
        rush: 0.24,
        movement: 0.22,
        cqb: 0.14,
        consistency: 0.1,
      }),
      recommendedDrillCategories: Object.freeze([
        'aim',
        'movement',
        'cqb',
        'entry',
        'pressure',
      ]),
      correctionPriorities: Object.freeze([
        'stabilize_vertical_snap_for_headline',
        'improve_first_contact_accuracy',
        'sustain_rush_control_under_pressure',
      ]),
      postAnalysisMapping: Object.freeze({
        snap_low: Object.freeze(['aim', 'cqb']),
        movement_low: Object.freeze(['movement', 'entry']),
        pressure_low: Object.freeze(['pressure', 'consistency']),
      }),
    }),
    miTone: Object.freeze({
      style: 'fast',
      tempo: 'aggressive',
      personaHint: 'capa_and_speed',
      communicationStyle: 'short_direct_hype',
      directiveStyle: 'rush_execution_command',
      vocabularyTone: 'mobile_rush_competitive',
      coachingAttitude: 'assertive_momentum',
    }),
    uiTerminology: Object.freeze({
      gameName: 'Free Fire',
      systemLabels: Object.freeze({
        coach: 'Coach de Capa',
        sensi: 'Ajuste de DPI',
        training: 'Treino de Rush',
      }),
      directives: Object.freeze({
        recharge: 'Sem energia. Recarregue para continuar subindo capa.',
        analysis: 'Sem clipes. Envie seu melhor rush para análise.',
        training: 'Mira tremendo. Treine o controle de disparo.',
        sensi: 'DPI desalinhada. Ajuste sua sensibilidade agora.',
      }),
      terms: Object.freeze({
        headshot: 'Capa',
        recoil: 'Recuo',
        aim: 'Mira',
        movement: 'Rush',
      }),
    }),
  }),
});

const GAME_ALIAS_TO_CANONICAL = Object.freeze({
  pubg_mobile: CANONICAL_GAMES.PUBG_MOBILE,
  pubg: CANONICAL_GAMES.PUBG_MOBILE,
  pubgm: CANONICAL_GAMES.PUBG_MOBILE,
  'pubg-mobile': CANONICAL_GAMES.PUBG_MOBILE,
  cod_mobile: CANONICAL_GAMES.COD_MOBILE,
  codm: CANONICAL_GAMES.COD_MOBILE,
  cod: CANONICAL_GAMES.COD_MOBILE,
  call_of_duty_mobile: CANONICAL_GAMES.COD_MOBILE,
  'cod-mobile': CANONICAL_GAMES.COD_MOBILE,
  free_fire: CANONICAL_GAMES.FREE_FIRE,
  freefire: CANONICAL_GAMES.FREE_FIRE,
  ff: CANONICAL_GAMES.FREE_FIRE,
  'free-fire': CANONICAL_GAMES.FREE_FIRE,
});

const normalizeToken = (value) =>
  (value == null ? '' : String(value))
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

export const isKnownGameId = (gameId) => {
  const token = normalizeToken(gameId);
  return Boolean(token && GAME_ALIAS_TO_CANONICAL[token]);
};

export const canonicalGameId = (gameId, fallback = DEFAULT_CANONICAL_GAME) => {
  const token = normalizeToken(gameId);
  if (!token) return fallback;
  return GAME_ALIAS_TO_CANONICAL[token] || fallback;
};

export const resolveGameProfile = (gameId) => {
  const canonical = canonicalGameId(gameId);
  return OFFICIAL_GAME_PROFILES[canonical] || OFFICIAL_GAME_PROFILES[DEFAULT_CANONICAL_GAME];
};

export const resolveCoachProfile = (gameId) => resolveGameProfile(gameId).coachProfile;
export const resolveSensitivityProfile = (gameId) => resolveGameProfile(gameId).sensitivityProfile;
export const resolveTrainingProfile = (gameId) => resolveGameProfile(gameId).trainingProfile;
export const resolveMiTone = (gameId) => resolveGameProfile(gameId).miTone;
export const resolveUiTerminology = (gameId) => resolveGameProfile(gameId).uiTerminology;

export const toLegacyGameContext = (gameId) =>
  canonicalGameId(gameId);

export const toTrainingProfileKey = (gameId) =>
  resolveGameProfile(gameId).trainingProfile.datasetKey;

export const toSensitivityProfileKey = (gameId) =>
  resolveGameProfile(gameId).sensitivityProfile.adapterKey;

export const toCoachProfileKey = (gameId) =>
  resolveGameProfile(gameId).coachProfile.engineProfile;

export const listCanonicalGames = () => Object.keys(OFFICIAL_GAME_PROFILES);

export const CANONICAL_GAME_PROFILES = OFFICIAL_GAME_PROFILES;
